import React, { useEffect, useCallback, useRef } from "react";
import LoaderComponent from "../../components/LoaderComponent/LoaderComponent";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { initialise, setSession } from "../../data/slices/authSlice";
import { message } from "antd";
import axiosInstance from "../../utils/axios";
import ltiAxios from "../../utils/ltiAxios";

/**
 * LtiVMAuthentication — handles LTI launches that target Linux or Windows
 * EC2 (ACK) desktops. The backend redirects here with:
 *
 *   /auth/lti/vm/linux?accessToken=...&instanceType=g4dn.xlarge&volumeSize=60&persistent=true
 *   /auth/lti/vm/windows?accessToken=...&instanceType=g4dn.xlarge&volumeSize=50&persistent=true
 *
 * Flow:
 *   1. Authenticate the user via the LTI access token (same as container flow).
 *   2. Call POST /api/linux or /api/windows with the instance params.
 *   3. Redirect to the platform-specific viewer.
 */
function LtiVMAuthentication() {
  const { vm_type } = useParams();          // "linux" or "windows"
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const accessToken  = searchParams.get("accessToken");
  const instanceType = searchParams.get("instanceType") || null;
  const volumeSize   = parseInt(searchParams.get("volumeSize"), 10) || null;
  const persistent   = searchParams.get("persistent") !== "false"; // default true
  const template     = searchParams.get("template") || null;       // e.g. "ubuntu-ack"

  const { isAuthenticated, user: authUser } = useSelector((s) => s.auth);
  const [loading, setLoading] = React.useState(true);
  // Guard against the effect firing twice: the auth dispatch flips
  // `isAuthenticated` from false → true mid-flow, which re-runs the effect
  // and would otherwise POST /api/linux a second time → duplicate VM.
  const launchStartedRef = useRef(false);

  const launchVM = useCallback(async (orgId) => {
    const defaultTemplate = vm_type === "windows" ? "windows-dcv" : "ubuntu-ack";
    const endpoint = vm_type === "windows" ? "windows" : "linux";

    try {
      // Reuse an existing desktop owned by this user if one is around — a
      // POST always provisions a fresh VM whose DCV daemon takes minutes to
      // bind 8443. That cold-boot window is exactly when the LTI viewer
      // tries to connect, so without this the user lands on a not-yet-
      // ready VM. The existing-desktop path lands on a warm DCV.
      const existingResp = await axiosInstance.get(endpoint).catch(() => null);
      const list = existingResp?.data?.desktops || existingResp?.data || [];
      const desktops = Array.isArray(list) ? list : [];
      // Prefer a Running desktop (warm DCV); fall back to any non-terminal
      // one (its DCV will be ready by the time the viewer's poll catches up).
      const isUsable = (d) => {
        const p = (d?.phase || "").toLowerCase();
        return p !== "" && p !== "failed" && p !== "terminated" && p !== "stopped";
      };
      const running = desktops.find((d) => (d?.phase || "").toLowerCase() === "running");
      const existing = running || desktops.find(isUsable);
      if (existing) {
        const id = existing.id || existing.name;
        if (id) {
          navigate(`/control/${endpoint}/${id}?lti=1`);
          return;
        }
      }

      const payload = {
        template: template || defaultTemplate,
        persistent,
      };
      if (instanceType) payload.instanceType = instanceType;
      if (volumeSize)   payload.volumeSize   = volumeSize;

      const res = await axiosInstance.post(endpoint, payload);
      const id = res.data?.id || res.data?.name;
      if (!id) throw new Error("No desktop ID returned");

      navigate(`/control/${endpoint}/${id}?lti=1`);
    } catch (err) {
      message.error(err?.response?.data?.message || err.message || "Failed to launch VM");
      setLoading(false);
    }
  }, [vm_type, template, instanceType, volumeSize, persistent, navigate]);

  useEffect(() => {
    if (vm_type !== "linux" && vm_type !== "windows") {
      message.error(`Unknown VM type: ${vm_type}`);
      setLoading(false);
      return;
    }

    async function authenticateAndLaunch() {
      try {
        setSession(accessToken);
        const res = await ltiAxios.get("profile/whoami");
        const user = res.data;
        axiosInstance.defaults.headers["X-User-ID"] = user?.username;
        dispatch(initialise({ isAuthenticated: true, user }));
        await launchVM(user?.orgId);
      } catch (err) {
        message.error("Token is invalid or expired");
        dispatch(initialise({ isAuthenticated: false, user: null }));
        window.localStorage.removeItem("gomydesk_token");
        setLoading(false);
      }
    }

    if (launchStartedRef.current) return;
    launchStartedRef.current = true;

    if (isAuthenticated) {
      launchVM(authUser?.orgId);
    } else if (accessToken) {
      authenticateAndLaunch();
    } else {
      message.error("No access token provided");
      setLoading(false);
    }
  }, [dispatch, accessToken, isAuthenticated, authUser?.orgId, vm_type, launchVM]);

  return (
    <div className="lti-auth">
      <div className="lti-loading">
        {loading ? <LoaderComponent /> : "VM launch failed — check the console for details"}
      </div>
    </div>
  );
}

export default LtiVMAuthentication;
